import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Card,
  Row,
  Col,
  Badge,
  Form,
  Pagination,
  Spinner,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

function PlotListPage() {
  const [plots, setPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagePath, setImagePath] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [singlePlot, setSinglePlot] = useState(null); // Single plot ke liye
  const [isSinglePlotView, setIsSinglePlotView] = useState(false); // Single view mode

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const project_id = searchParams.get("project_id");
  const block_id = searchParams.get("block_id");
  const plot_id = searchParams.get("plot_id"); // Plot ID

  const handleBookNow = (plot_id) => {
    navigate(
      `/create-property-lead?project_id=${project_id}&block_id=${block_id}&plot_id=${plot_id}`,
    );
  };

  const getAuthToken = () => localStorage.getItem("token");

  // Fetch single plot detail
  const fetchSinglePlot = async () => {
    if (!plot_id) return;

    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}/single-plot-detail?plot_id=${plot_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();

      if (data.status === "1") {
        setSinglePlot(data.data);
        setIsSinglePlotView(true);
        setPlots([]);
      } else {
        // Agar plot nahi mila toh saare plots fetch karein
        fetchPlots(currentPage);
      }
    } catch (err) {
      console.error("Error fetching single plot:", err);
      fetchPlots(currentPage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all plots
  const fetchPlots = async (page = 1) => {
    setLoading(true);
    setIsSinglePlotView(false);
    setSinglePlot(null);

    try {
      const token = getAuthToken();
      let url = `${API_URL}/plot-list?page=${page}&limit=10`;

      if (project_id) {
        url += `&project_id=${project_id}`;
      }
      if (block_id && block_id !== "null") {
        url += `&block_id=${block_id}`;
      }

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setPlots(data.data || []);
      setImagePath(data.imagePath || "");
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching plots:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (plot_id) {
      // Agar plot_id hai toh single plot fetch karein
      fetchSinglePlot();
    } else if (project_id && block_id) {
      // Nahi toh saare plots fetch karein
      fetchPlots(currentPage);
    }
  }, [project_id, block_id, plot_id, currentPage]);

  const toSentenceCase = (text) => {
    if (!text) return "-";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Back to all plots
  const handleBackToAllPlots = () => {
    navigate(
      `/plot-list?project_id=${project_id}&block_id=${block_id || "null"}`,
    );
  };

  // Render Single Plot Detail
  const renderSinglePlot = () => {
    if (!singlePlot) return null;

    return (
      <div className="single-plot-detail">
        <Card>
          <Card.Body>
            <Row className="plot-details-row">
              <Col md={6}>
                <div className="detail-item">
                  <strong>Plot/Shop/Villa No:</strong>
                  <span>{singlePlot.plot_shop_villa_no || "N/A"}</span>
                </div>

                <div className="detail-item">
                  <strong>Project Name:</strong>
                  <span>{singlePlot.project_name?.toUpperCase() || "N/A"}</span>
                </div>

                <div className="detail-item">
                  <strong>Block Name:</strong>
                  <span>{singlePlot.block_name?.toUpperCase() || "N/A"}</span>
                </div>

                <div className="detail-item">
                  <strong>Dimension:</strong>
                  <span>{singlePlot.dimension || "N/A"}</span>
                </div>

                <div className="detail-item">
                  <strong>Area:</strong>
                  <span>
                    {singlePlot.area_sqyd
                      ? `${singlePlot.area_sqyd} SQ.Yd.`
                      : "N/A"}
                  </span>
                </div>
              </Col>

              <Col md={6}>
                <div className="detail-item">
                  <strong>Rate:</strong>
                  <span>
                    {singlePlot.rate
                      ? `₹ ${Number(singlePlot.rate).toLocaleString("en-IN")} / SQ.Yd`
                      : "N/A"}
                  </span>
                </div>

                <div className="detail-item total-amount-item">
                  <strong>Total Amount:</strong>
                  <span>
                    {singlePlot.area_sqyd && singlePlot.rate
                      ? `₹ ${(
                          Number(singlePlot.area_sqyd) * Number(singlePlot.rate)
                        ).toLocaleString("en-IN")}`
                      : "N/A"}
                  </span>
                </div>

                <div className="detail-item">
                  <strong>Facing:</strong>
                  <span>{singlePlot.facing || "N/A"}</span>
                </div>

                <div className="detail-item">
                  <strong>Document Type:</strong>
                  <span>
                    {singlePlot.document_type
                      ? toSentenceCase(singlePlot.document_type)
                      : "N/A"}
                  </span>
                </div>

                <div className="detail-item">
                  <strong>Status:</strong>

                  <span
                    className={`plot-detail-status ${
                      singlePlot.status?.toLowerCase() === "available"
                        ? "status-available"
                        : singlePlot.status?.toLowerCase() === "booked" ||
                            singlePlot.status?.toLowerCase() === "active"
                          ? "status-booked"
                          : "status-sold"
                    }`}
                  >
                    {singlePlot.status
                      ? toSentenceCase(singlePlot.status)
                      : "N/A"}
                  </span>
                </div>
              </Col>
            </Row>

            {/* Dimensions */}
            {(singlePlot.north ||
              singlePlot.south ||
              singlePlot.east ||
              singlePlot.west) && (
              <Row className="dimension-row">
                <Col>
                  <div className="dimension-box">
                    <h6 className="dimension-title">Dimensions Details</h6>

                    <div className="dimension-list">
                      {singlePlot.north && (
                        <div className="dimension-item">
                          <span className="dimension-label">North</span>
                          <span className="dimension-value">
                            {singlePlot.north}
                          </span>
                        </div>
                      )}

                      {singlePlot.south && (
                        <div className="dimension-item">
                          <span className="dimension-label">South</span>
                          <span className="dimension-value">
                            {singlePlot.south}
                          </span>
                        </div>
                      )}

                      {singlePlot.east && (
                        <div className="dimension-item">
                          <span className="dimension-label">East</span>
                          <span className="dimension-value">
                            {singlePlot.east}
                          </span>
                        </div>
                      )}

                      {singlePlot.west && (
                        <div className="dimension-item">
                          <span className="dimension-label">West</span>
                          <span className="dimension-value">
                            {singlePlot.west}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* Book Now Button */}
            {singlePlot.status?.toLowerCase() === "available" && (
              <div className="mt-4">
                <button
                  className="booknow w-100"
                  onClick={() => handleBookNow(singlePlot.id)}
                  style={{ padding: "12px", fontSize: "18px" }}
                >
                  Book Now
                </button>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <div className="padding_15 px-0">
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center justify-content-between">
            <div className="titlepage">
              <h3>
                {isSinglePlotView ? "Plot Details" : "Project & Units Details"}
              </h3>
            </div>
            <button
              className="btn btn-primary border border-white"
              onClick={handleBackToAllPlots}
            >
              ← Back to All Plots
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* Search - Only show in list view */}
          {!isSinglePlotView && (
            <Form.Group className="mb-4">
              <Form.Control
                type="text"
                placeholder="Search by Project Name or Block Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : isSinglePlotView ? (
            // Single Plot View
            renderSinglePlot()
          ) : plots.length > 0 ? (
            <>
              <div className="row">
                {plots
                  .filter((plot) => {
                    const search = searchTerm.trim().toLowerCase();
                    return (
                      plot.project_name?.toLowerCase().includes(search) ||
                      plot.block_name?.toLowerCase().includes(search)
                    );
                  })
                  .map((plot) => (
                    <div className="col-md-4 col-lg-4 col-12" key={plot.id}>
                      <div className="card">
                        <div className="card-body plot-card">
                          <div className="mb-3">
                            <p className="data_plot">
                              <strong>Project Name:</strong>{" "}
                              {plot.project_name?.toUpperCase()}
                            </p>
                            <p className="data_plot">
                              <strong>Block Name:</strong>{" "}
                              {plot.block_name?.toUpperCase()}
                            </p>
                          </div>

                          <h5 className="d-flex justify-content-between align-items-center border-top pt-2">
                            <span>
                              <strong>Units/Shop/Villa No:</strong>
                            </span>
                            <span>{plot.plot_shop_villa_no}</span>
                          </h5>

                          <div className="mt-3">
                            <p className="data_plot">
                              <strong>Block Name :</strong> {plot.block_name}
                            </p>
                            <p className="data_plot">
                              <strong>Facing :</strong> {plot.facing}
                            </p>
                            <p className="data_plot">
                              <strong>Area :</strong> {plot.area_sqyd} SQ.Yd.
                            </p>
                            <p className="data_plot">
                              <strong>Rate:</strong> {plot.rate} Per SQ.Yd
                            </p>
                            <p className="data_plot">
                              <strong>Total Amount:</strong> ₹{" "}
                              {plot.area_sqyd * plot.rate}
                            </p>
                            <p className="data_plot">
                              <strong>Document Type:</strong>{" "}
                              {toSentenceCase(plot.document_type)}
                            </p>
                            <p className="data_plot">
                              <strong>Dimension:</strong>{" "}
                              {plot.north ||
                              plot.south ||
                              plot.east ||
                              plot.west ? (
                                <>
                                  {plot.north && `North: ${plot.north}`}
                                  {plot.south && `, South: ${plot.south}`}
                                  {plot.east && `, East: ${plot.east}`}
                                  {plot.west && `, West: ${plot.west}`}
                                </>
                              ) : (
                                "N/A"
                              )}
                            </p>
                            <p
                              className="data_plot"
                              style={{
                                color:
                                  plot.status?.toLowerCase() === "available"
                                    ? "green"
                                    : plot.status?.toLowerCase() === "sold"
                                      ? "red"
                                      : "inherit",
                              }}
                            >
                              <strong>Status:</strong>{" "}
                              {toSentenceCase(plot.status)}
                            </p>
                          </div>
                        </div>
                        <div className="card-footer bg-white w-auto">
                          {plot.status?.toLowerCase() === "available" && (
                            <div className="d-flex justify-content-end">
                              <button
                                className="booknow"
                                onClick={() => handleBookNow(plot.id)}
                              >
                                Book Now
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Pagination */}
              <div className="pagination">
                {totalPages > 1 && (
                  <Pagination className="justify-content-center pagination_design">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-muted">
              No units found for this block.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PlotListPage;
